import * as angular from "angular";
import { Filter } from "../models/filter.model";
import { CanvassStatus } from "../models/canvass-status.model";
import { User } from "../models/user.model";
import { MarkerService } from "../components/map/marker.helper";
import { CanvassDataProvider } from "./canvass.dp";
import { CanvassResultService } from "../services/result.service";
import { UserService } from "../services/users.service";
import { House } from "../models/house.model";

export class CanvasserTrackingDataProvider {
    public markers: object[] = [];
    public filter: any = [];
    public allLoaded = false;
    public limit = 500;

    private previousMarkers: object[] = [];
    private usersPromise: any;
    private onlineUsersPromise: any;
    private scrollPromise: any;
    public users: User[] | null = null;
    public onlineUsers: User[] | null = null;
    public usersLoading: boolean = true;
    public selectedCanvassId: number;

    private $q: any;
    private modelsUtils: any;
    private $filter: any;

    private userService: UserService;
    private canvassStatus: CanvassStatus;
    private markerService: MarkerService;
    private canvassDataProvider: CanvassDataProvider;
    private canvassResultService: CanvassResultService;

    constructor(
        $q: any,
        UserService: UserService,
        MarkerService: MarkerService,
        CanvassDataProvider: CanvassDataProvider,
        CanvassResultService: CanvassResultService,
        ModelsUtils: any,
        $filter: any
    ) {
        this.$q = $q;
        this.modelsUtils = ModelsUtils;
        this.$filter = $filter;

        this.userService = UserService;
        this.canvassStatus = new CanvassStatus();
        this.markerService = MarkerService;
        this.canvassDataProvider = CanvassDataProvider;
        this.canvassResultService = CanvassResultService;
    }

    getAllUsers(filter: any, offset?: number): Promise<any> {
        const deferred = this.$q.defer();
        let data: any = {
            filters: angular.copy(filter),
            limit: this.limit,
            onlyWithCanvasses: true,
            offset: offset ?? 0,
        };

        this.userService.all(data).then(
            (response) => {
                const json = response["data"];
                const users = [];

                for (let i = 0; i < json.data.length; i++) {
                    let newUser = this.modelsUtils.user(json.data[i]);
                    users.push(newUser);
                }

                deferred.resolve(users);
            },
            (error) => {
                deferred.reject(error);
            }
        );

        return deferred.promise;
    }

    getAllUsersFromCampaignExtra(filter: any, offset?: number): Promise<any> {
        const deferred: any = this.$q.defer();
        let data: any = {
            filters: angular.copy(filter),
            limit: this.limit,
            onlyWithCanvasses: true,
            offset: offset ?? 0,
        };

        this.userService.allFromCampaignExtra(data).then(
            (response: object): void => {
                const json: any = response["data"];
                const users: any[] = [];

                for (let i: number = 0; i < json.data.length; i++) {
                    let newUser: any = this.modelsUtils.user(json.data[i]);
                    users.push(newUser);
                }

                deferred.resolve(users);
            },
            (error: any): void => {
                deferred.reject(error);
            }
        );

        return deferred.promise;
    }

    getOnlineUsers(): Promise<any> {
        if (this.onlineUsers !== null && !scroll)
            return this.$q.resolve(this.onlineUsers);
        if (this.onlineUsersPromise && !scroll) return this.onlineUsersPromise;

        const deferred: any = this.$q.defer();
        this.userService.onlineNow({}).then(
            (response: object): void => {
                const json: any = response["data"];
                this.onlineUsers = [];

                for (let i = 0; i < json.data.length; i++) {
                    let newUser = this.modelsUtils.user(json.data[i]);
                    this.onlineUsers.push(newUser);
                }

                this.onlineUsersPromise = null;
                deferred.resolve(this.onlineUsers);
            },
            (error: any): void => {
                this.onlineUsers = [];
                deferred.reject(error);
            }
        );

        this.onlineUsersPromise = deferred.promise;
        return this.onlineUsersPromise;
    }

    expandUser(users: any, markers: any, uid: string | number): void {
        this.selectedCanvassId = null;
        angular.forEach(users, (user: User): void => {
            if (user.uid == uid) {
                if (user.expanded) {
                    this.clearMarkers(markers);
                    this.setMarkers(markers, users);
                    user.expanded = false;
                } else {
                    user.expanded = true;
                    let filters = new Filter({
                        uids: { values: [uid], checked: true },
                        types: { values: ["canvass"], checked: true },
                        startDate: {
                            values: this.filter.dateRange.dateStart,
                            checked: true,
                        },
                        endDate: {
                            values: this.filter.dateRange.dateEnd,
                            checked: true,
                        },
                        effort: {
                            values: this.filter.effort,
                            checked: true,
                        },
                        limit: { values: "none", checked: true },
                    });
                    if (user.canvasses) {
                        this.clearMarkers(markers);
                        user.formattedCanvasses[0].expanded = false;
                        this.expandDay(
                            markers,
                            user.formattedCanvasses[0],
                            user
                        );
                    } else {
                        this.canvassDataProvider
                            .getAllUserCanvasses(filters)
                            .then((canvasses) => {
                                user.canvasses = canvasses;
                                user.formattedCanvasses =
                                    this.formatCanvasses(canvasses);

                                this.clearMarkers(markers);
                                this.expandDay(
                                    markers,
                                    user.formattedCanvasses[0],
                                    user
                                );
                            });
                    }
                }
            } else {
                user.expanded = false;
            }
        });
    }

    expandDay(markers, data: any, user: User) {
        this.selectedCanvassId = null;
        this.clearMarkers(markers);
        if (!data.expanded) {
            angular.forEach(user.formattedCanvasses, (day: any) => {
                day.expanded = false;
            });

            data.expanded = true;
            this.setMarkers(markers, data.canvasses, user);
        } else {
            data.expanded = false;
        }
    }

    clearUsers(users): void {
        if (users) users.splice(0, users.length);
    }

    loadOverview(filter: any): Promise<any> {
        const deferred = this.$q.defer();
        filter.needs_teams = true;

        this.canvassResultService.overview(filter).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                }
            },
            (error) => deferred.reject(error)
        );

        return deferred.promise;
    }

    setMarkers(markers, items: any[], user?: User): object[] {
        if (!markers) {
            markers = [];
        }
        let foundFirstCanvassWithLocation = false;
        angular.forEach(items, (item) => {
            if (item.csid) {
                if (item.latitude && item.longitude) {
                    if (!foundFirstCanvassWithLocation) {
                        foundFirstCanvassWithLocation = true;
                        // The user's latest canvass carries their avatar,
                        // rendered inside the same pin as the user markers
                        let marker: any = this.generateCanvassMarker(
                            item,
                            this.markerService.generateUserMarkerSVG(user)
                        );
                        marker.options.icon.scaledSize = {
                            width: 48,
                            height: 55,
                        };
                        marker.options.pinIcon = true;
                        this.markerService.applyUserPhotoIcon(marker, user);
                        markers.push(marker);
                    } else {
                        markers.push(this.generateCanvassMarker(item));
                    }
                }
            } else if (
                item.dataType &&
                (item.dataType == "campaign" || item.dataType == "division")
            ) {
                markers.push(
                    this.markerService.generateCampaignDivisionMarker(item)
                );
            } else {
                if (
                    item.last_canvass &&
                    item.last_canvass.latitude &&
                    item.last_canvass.longitude
                ) {
                    markers.push(
                        this.markerService.generateUserMarker(
                            item,
                            item.last_canvass.latitude,
                            item.last_canvass.longitude
                        )
                    );
                }
            }
        });
        return markers;
    }

    generateCanvassMarker(canvass: any, userImage?: string): object {
        let width = 24;
        let icon =
            "/img/map/markers/d" +
            this.canvassStatus.getColorAlias(canvass.color) +
            ".svg";

        if (userImage) {
            width = 32;
            icon = userImage;
        }

        let title = "";
        let personOrHouse = "";
        if (canvass.houseoccupant && canvass.houseoccupant.hofname) {
            personOrHouse =
                canvass.houseoccupant.hofname +
                " " +
                canvass.houseoccupant.holname;
        } else if (canvass.house && canvass.house.haddress) {
            personOrHouse =
                canvass.house.hnumber +
                " " +
                canvass.house.hname +
                " " +
                canvass.house.haddress;
        }

        canvass.personOrHouse = personOrHouse;
        title += personOrHouse ? personOrHouse + " - " : "";
        title += canvass.cssname;

        if (canvass.cssid == 1) {
            title += " - " + canvass.csreception + " star";
        }

        canvass.formattedDate = this.$filter("niceShortDateTime")(
            canvass.cscreationdatetime
        );

        canvass.ratingString = "";
        if (canvass.csreception) {
            canvass.ratingString = "<div style='margin: 5px 0'>";
            for (let i = 0; i < canvass.csreception; i++) {
                canvass.ratingString += "⭐ ";
            }
            canvass.ratingString += "</div>";
        }

        title += " - " + canvass.formattedDate;
        return {
            id: "canvass" + canvass.csid,
            options: {
                icon: {
                    url: icon,
                    scaledSize: {
                        width: width,
                        height: 32,
                    },
                },
                title: title,
                userIcon: !!userImage,
            },
            showInfoWindow: true,
            canvass: canvass,
            coords: {
                latitude: canvass.latitude,
                longitude: canvass.longitude,
            },
        };
    }

    generateHouseMarker(
        house: House,
        latitude: string | number,
        longitude: string | number
    ): object {
        let title = "Location we have saved for " + house.hnumber;
        if (house.hname) title += " " + house.hname;
        title += " " + house.haddress;

        return {
            id: "house" + house.hid,
            options: {
                icon: {
                    url: "/img/map/markers/home-circle-grey.png",
                    scaledSize: {
                        width: 32,
                        height: 32,
                    },
                },
                zIndex: 999,
                title: title,
                userIcon: true,
            },
            coords: {
                latitude: latitude,
                longitude: longitude,
            },
        };
    }

    clearMarkers(markers): void {
        markers.splice(0, markers.length);
    }

    resetMarkers(markers): void {
        if (markers) {
            angular.forEach(markers, (marker: any) => {
                if (marker.options.pinIcon) {
                    marker.options.icon.scaledSize = { width: 48, height: 55 };
                } else if (marker.options.userIcon) {
                    marker.options.icon.scaledSize = { width: 32, height: 32 };
                } else {
                    marker.options.icon.scaledSize = { width: 24, height: 32 };
                }
            });
            angular.forEach(this.users, (user: User) => {
                angular.forEach(user.canvasses, (canvass: any) => {
                    canvass.selected = false;
                });
            });
        }
    }

    expandCanvass(markers: object[], id: string | number): void {
        this.resetMarkers(markers);

        angular.forEach(markers, (marker: any, $index: number) => {
            if (marker.id.replace("canvass", "") == id) {
                marker.options.zIndex = 998;
                if (marker.options.pinIcon) {
                    marker.options.icon.scaledSize = { width: 58, height: 66 };
                } else if (marker.options.userIcon) {
                    marker.options.icon.scaledSize = { width: 48, height: 48 };
                } else {
                    marker.options.icon.scaledSize = { width: 36, height: 48 };
                }
            } else if (marker.id.includes("canvass")) {
                marker.options.zIndex = markers.length - $index;
                if (marker.options.pinIcon) {
                    marker.options.icon.scaledSize = { width: 48, height: 55 };
                } else if (marker.options.userIcon) {
                    marker.options.icon.scaledSize = { width: 32, height: 32 };
                } else {
                    marker.options.icon.scaledSize = { width: 24, height: 32 };
                }
            }
        });

        angular.forEach(this.users, (user: User) => {
            angular.forEach(user.canvasses, (canvass: any) => {
                canvass.selected = canvass.csid == id;
            });
        });
    }

    formatCanvasses(canvasses: any[]): object[] {
        let formattedCanvasses = [];
        let previousDate = null;
        let thisDate = null;
        let prevDateCanvasses = [];

        angular.forEach(canvasses, (canvass) => {
            thisDate = new Date(canvass.cscreationdatetime);
            if (previousDate && previousDate.getDate() !== thisDate.getDate()) {
                formattedCanvasses.push({
                    expanded: false,
                    gps_stats: this.getGpsStats(prevDateCanvasses),
                    canvasses: prevDateCanvasses,
                    filtered_canvasses: prevDateCanvasses,
                });
                prevDateCanvasses = [];
            }
            canvass.distance = this.distance(canvass);
            prevDateCanvasses.push(canvass);
            previousDate = new Date(canvass.cscreationdatetime);
        });

        formattedCanvasses.push({
            expanded: false,
            gps_stats: this.getGpsStats(prevDateCanvasses),
            canvasses: prevDateCanvasses,
            filtered_canvasses: prevDateCanvasses,
        });
        prevDateCanvasses = [];

        return formattedCanvasses;
    }

    getGpsStats(canvassses: any): any {
        let stats = {
            total: canvassses.length,
            verified: 0,
            review: 0,
            flagged: 0,
            no_gps: 0,
        };

        angular.forEach(canvassses, (canvass) => {
            if (!canvass.accuracy || canvass.accuracy > 100) {
                stats.no_gps += 1;
            } else if (canvass.distance == null || canvass.distance < 100) {
                stats.verified += 1;
            } else if (canvass.distance < 300) {
                stats.review += 1;
            } else stats.flagged += 1;
        });

        return stats;
    }

    distance(canvass: any): number | null {
        if (
            canvass.latitude == null ||
            canvass.longitude == null ||
            canvass.house_latitude == null ||
            canvass.house_longitude == null
        ) {
            // No canvass GPS or no house attached — nothing to measure against
            return null;
        }

        let lat1 = canvass.latitude;
        let lon1 = canvass.longitude;
        let lat2 = canvass.house_latitude;
        let lon2 = canvass.house_longitude;
        let p = 0.017453292519943295; // Math.PI / 180
        let c = Math.cos;
        let a =
            0.5 -
            c((lat2 - lat1) * p) / 2 +
            (c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p))) / 2;

        return 12742 * Math.asin(Math.sqrt(a)) * 1000; // 2 * R; R = 6371 km then * by 1000 to get metres
    }
}

angular
    .module("vcDataProvider")
    .service("CanvasserTrackingDataProvider", CanvasserTrackingDataProvider);
